import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { BE_API_AUTH_URL } from '../constants/app.constants';

export type EmailCampaignStatus = 'draft' | 'ready' | 'sending' | 'sent' | 'failed' | 'archived';

export interface EmailCampaignBlock {
  type: 'hero' | 'text' | 'image' | 'feature_list' | 'cta' | 'divider' | 'footer_note' | string;
  content: any;
}

export interface EmailCampaign {
  _id: string;
  name: string;
  status: EmailCampaignStatus;
  templateId: string | null;
  subject: string;
  preheader: string;
  senderName: string;
  senderEmail: string;
  replyTo: string;
  blocks: EmailCampaignBlock[];
  updatedAt: string;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class EmailCampaignService {
  constructor(private http: HttpClient, private authService: AuthService) {}

  listCampaigns(params?: { search?: string; status?: string }): Observable<EmailCampaign[]> {
    let httpParams = new HttpParams();
    if (params?.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params?.status) {
      httpParams = httpParams.set('status', params.status);
    }
    return this.http
      .get<EmailCampaign[]>(`${BE_API_AUTH_URL}/admin/email-campaigns`, { headers: this.authService.createAuthorizationHeader(), params: httpParams })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  createCampaign(payload: Partial<EmailCampaign>): Observable<EmailCampaign> {
    return this.http
      .post<EmailCampaign>(`${BE_API_AUTH_URL}/admin/email-campaigns`, payload, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCampaign(id: string): Observable<EmailCampaign> {
    return this.http
      .get<EmailCampaign>(`${BE_API_AUTH_URL}/admin/email-campaigns/${id}`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateCampaign(id: string, payload: Partial<EmailCampaign>): Observable<EmailCampaign> {
    return this.http
      .put<EmailCampaign>(`${BE_API_AUTH_URL}/admin/email-campaigns/${id}`, payload, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  previewCampaign(id: string): Observable<{ html: string; text: string; allowedBlockTypes: string[] }> {
    return this.http
      .post<{ html: string; text: string; allowedBlockTypes: string[] }>(`${BE_API_AUTH_URL}/admin/email-campaigns/${id}/preview`, {}, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  sendTestEmail(id: string, email: string): Observable<any> {
    return this.http
      .post(`${BE_API_AUTH_URL}/admin/email-campaigns/${id}/test-send`, { email }, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  sendCampaign(id: string, payload: {
    singleEmail?: string;
    pastedEmails?: string;
    emails?: string[];
    userIds?: string[];
    idempotencyKey?: string;
  }): Observable<any> {
    return this.http
      .post(`${BE_API_AUTH_URL}/admin/email-campaigns/${id}/send`, payload, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  listSends(id: string): Observable<any[]> {
    return this.http
      .get<any[]>(`${BE_API_AUTH_URL}/admin/email-campaigns/${id}/sends`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  listRecipients(id: string, sendId: string, page = 1, pageSize = 50): Observable<any> {
    const params = new HttpParams().set('page', String(page)).set('pageSize', String(pageSize));
    return this.http
      .get(`${BE_API_AUTH_URL}/admin/email-campaigns/${id}/sends/${sendId}/recipients`, { headers: this.authService.createAuthorizationHeader(), params })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  listTemplates(): Observable<any[]> {
    return this.http
      .get<any[]>(`${BE_API_AUTH_URL}/admin/email-campaign-templates`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  uploadAsset(file: File, campaignId?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (campaignId) {
      formData.append('campaignId', campaignId);
    }

    return this.http
      .post(`${BE_API_AUTH_URL}/admin/email-campaign-assets/upload`, formData, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  searchUsers(search = '', page = 1, pageSize = 20): Observable<any> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    if (search) {
      params = params.set('search', search);
    }

    return this.http
      .get(`${BE_API_AUTH_URL}/admin/email-campaign-recipients/users`, { headers: this.authService.createAuthorizationHeader(), params })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }
}
